import { useCallback, useMemo, useState } from 'react'
import { Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  checkForUpdateAsync,
  fetchUpdateAsync,
  reloadAsync,
  useUpdates,
} from 'expo-updates'
import AppConfig from 'config'

const UPDATE_STATE = {
  initial: 'initial',
  updateNotAvailable: 'updateNotAvailable',
  checkingForUpdates: 'checkingForUpdates',
  updateFoundDownloading: 'updateFoundDownloading',
  updateDownloadedApplying: 'updateDownloadedApplying',
  updateDownloadFailed: 'updateDownloadFailed',
  noUpdatesAvailable: 'noUpdatesAvailable',
  updateCheckFailed: 'updateCheckFailed',
}

type UpdateState = (typeof UPDATE_STATE)[keyof typeof UPDATE_STATE]

export const useErrorScreenUpdate = () => {
  const { t } = useTranslation(['updateErrors'])

  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false)
  const [isDownloadingUpdate, setIsDownloadingUpdate] = useState(false)
  const [updateState, setUpdateState] = useState<UpdateState>(
    UPDATE_STATE.initial
  )
  const { isUpdatePending } = useUpdates()

  const isDevMode = AppConfig.isDev

  const checkForUpdate = useCallback(async () => {
    if (Platform.OS === 'web') {
      setUpdateState(UPDATE_STATE.updateNotAvailable)
      return
    }

    // Mock update flow for dev purposes
    if (isDevMode) {
      try {
        setIsCheckingUpdate(true)
        setUpdateState(UPDATE_STATE.checkingForUpdates)
        await new Promise((resolve) => setTimeout(resolve, 2000))
        setUpdateState(UPDATE_STATE.updateFoundDownloading)
        setIsDownloadingUpdate(true)
        await new Promise((resolve) => setTimeout(resolve, 3000))
        setUpdateState(UPDATE_STATE.updateDownloadedApplying)
        setTimeout(() => {
          setUpdateState(UPDATE_STATE.updateDownloadedApplying)
          reloadAsync()
        }, 2000)
      } catch (err) {
        console.error('Failed to check for update:', err)
        setUpdateState(UPDATE_STATE.updateCheckFailed)
      } finally {
        setIsCheckingUpdate(false)
        setIsDownloadingUpdate(false)
      }
      return
    }

    try {
      setIsCheckingUpdate(true)
      setUpdateState(UPDATE_STATE.checkingForUpdates)
      const result = await checkForUpdateAsync()
      if (result.isAvailable) {
        setUpdateState(UPDATE_STATE.updateFoundDownloading)
        setIsDownloadingUpdate(true)
        // Automatically download the update
        const update = await fetchUpdateAsync()
        if (update) {
          setUpdateState(UPDATE_STATE.updateDownloadedApplying)
          reloadAsync()
        } else {
          setUpdateState(UPDATE_STATE.updateDownloadFailed)
        }
      } else {
        setUpdateState(UPDATE_STATE.noUpdatesAvailable)
      }
    } catch (err) {
      console.error('Failed to check for update:', err)
      setUpdateState(UPDATE_STATE.updateCheckFailed)
    } finally {
      setIsCheckingUpdate(false)
      setIsDownloadingUpdate(false)
    }
  }, [t, isDevMode])

  const clearMessage = useCallback(() => {
    setUpdateState(UPDATE_STATE.initial)
  }, [])

  const updateMessage =
    updateState === UPDATE_STATE.initial ? '' : t(`updateErrors:${updateState}`)

  return {
    isCheckingUpdate,
    isDownloadingUpdate,
    isUpdatePending,
    updateMessage,
    checkForUpdate,
    clearMessage,
  }
}
